// Environment Configuration System
// Automatically loads the right configuration based on the current environment

export interface EnvironmentConfig {
  // App Configuration
  appEnv: string;
  appTitle: string;
  debugMode: boolean;
  logLevel: string;
  
  // API Configuration
  apiBaseUrl: string;
  socketUrl: string;
  
  // External APIs
  openaiApiKey: string;
  astrologyApiKey: string;
  astrologyUserId: string;
  
  // Payment Configuration
  razorpayKeyId: string;
  
  // Feature Flags
  enableDebugPanel: boolean;
  enableLogging: boolean;
  enableAnalytics: boolean;
}

// Default configuration (fallback)
const defaultConfig: EnvironmentConfig = {
  appEnv: 'development',
  appTitle: 'Rraasi (Development)',
  debugMode: true,
  logLevel: 'debug',
  
  apiBaseUrl: 'http://localhost:3000',
  socketUrl: 'http://localhost:3000',
  
  openaiApiKey: '',
  astrologyApiKey: '',
  astrologyUserId: '',
  
  razorpayKeyId: '',
  
  enableDebugPanel: true,
  enableLogging: true,
  enableAnalytics: false,
};

// Load configuration from environment variables
export const config: EnvironmentConfig = { ...defaultConfig };

// Load environment-specific values
if (import.meta.env.VITE_APP_ENV) config.appEnv = import.meta.env.VITE_APP_ENV;
if (import.meta.env.VITE_APP_TITLE) config.appTitle = import.meta.env.VITE_APP_TITLE;
if (import.meta.env.VITE_DEBUG_MODE) config.debugMode = import.meta.env.VITE_DEBUG_MODE === 'true';
if (import.meta.env.VITE_LOG_LEVEL) config.logLevel = import.meta.env.VITE_LOG_LEVEL;

// Load API configuration
if (import.meta.env.VITE_API_BASE_URL) config.apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
if (import.meta.env.VITE_SOCKET_URL) config.socketUrl = import.meta.env.VITE_SOCKET_URL;

// Load external API keys
if (import.meta.env.VITE_OPENAI_API_KEY) config.openaiApiKey = import.meta.env.VITE_OPENAI_API_KEY;
if (import.meta.env.VITE_ASTROLOGY_API_KEY) config.astrologyApiKey = import.meta.env.VITE_ASTROLOGY_API_KEY;
if (import.meta.env.VITE_ASTROLOGY_USER_ID) config.astrologyUserId = import.meta.env.VITE_ASTROLOGY_USER_ID;

// Load payment configuration
if (import.meta.env.VITE_RAZORPAY_KEY_ID) config.razorpayKeyId = import.meta.env.VITE_RAZORPAY_KEY_ID;

// Load feature flags
if (import.meta.env.VITE_ENABLE_DEBUG_PANEL) config.enableDebugPanel = import.meta.env.VITE_ENABLE_DEBUG_PANEL === 'true';
if (import.meta.env.VITE_ENABLE_LOGGING) config.enableLogging = import.meta.env.VITE_ENABLE_LOGGING === 'true';
if (import.meta.env.VITE_ENABLE_ANALYTICS) config.enableAnalytics = import.meta.env.VITE_ENABLE_ANALYTICS === 'true';

// Environment detection helpers
export const isDevelopment = config.appEnv === 'development' || import.meta.env.DEV;
export const isProduction = config.appEnv === 'production' || import.meta.env.PROD;
export const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Log configuration for debugging
if (config.enableLogging) {
  console.log('🌐 Environment Configuration Loaded:', {
    environment: config.appEnv,
    isDevelopment,
    isProduction,
    isLocalhost,
    apiBaseUrl: config.apiBaseUrl,
    socketUrl: config.socketUrl,
    debugMode: config.debugMode,
    logLevel: config.logLevel,
    razorpayKeyId: config.razorpayKeyId ? '***configured***' : 'not configured',
  });
}
